// Top-level PixiJS app + post-FX. Hosts one or two playfields.

import { Application, Container, Graphics, NoiseFilter, Sprite, Texture } from 'pixi.js';
import {
  AdvancedBloomFilter, CRTFilter, RGBSplitFilter,
} from 'pixi-filters';
import gsap from 'gsap';

import { fbm } from '../utils/noise';

import {
  BOARD_VISIBLE_HEIGHT, BOARD_WIDTH,
} from '../config';
import type { Engine } from '../game/engine';
import type { ThemePalette } from '../render/palettes';
import type { PostFxSettings } from '../render/postfx';
import type { RenderOptions } from '../render/renderer';
import { Playfield, type PlayfieldOptions } from './playfield';

export type LayoutMode = 'single' | 'versus' | 'triple' | 'royal';

export interface PixiAppOptions {
  palette: ThemePalette;
  renderOpts: RenderOptions;
  postFx: PostFxSettings;
  renderScale: number;
}

export class PixiApp {
  app: Application;
  ready = false;
  private layoutMode: LayoutMode = 'single';
  private palette: ThemePalette;
  private renderOpts: RenderOptions;
  private postFxSettings: PostFxSettings;
  private renderScale: number;

  // Stage layers
  private bgStage = new Container();
  private playStage = new Container();
  private fxStage = new Container();
  /** Filtered render group — everything visual that should receive
   *  bloom / CRT / film grain lives inside this container. Sibling
   *  to hudRoot so the HUD escapes the global post-fx chain
   *  (text in the bloom layer reads as washed-out / grainy). */
  private fxRoot = new Container();
  /** Unfiltered overlay group — receives per-playfield hudLayer and
   *  accoladesLayer at construction time. Rendered after fxRoot so
   *  text and chrome composite on top of the filtered scene. */
  private hudRoot = new Container();

  // Background — procedurally-rendered noise-warped gradient.
  // Each palette renders a deterministic backdrop into a Texture and the
  // sprite stays static. Theme switches regenerate it; no animation.
  private bgSprite: Sprite | null = null;
  /** Cached backdrop texture so resizes don't force a re-render. */
  private bgTexture: Texture | null = null;
  private bgTextureFor = '';

  // Playfields
  playerField!: Playfield;
  /** Primary AI playfield — kept as a direct field so older code paths
   *  (e.g. countdown overlay broadcasts to "the AI") still resolve to a
   *  sensible field. For multi-AI modes this is aiFields[0]. */
  aiField: Playfield | null = null;
  /** All AI fields (0-3). aiFields[0] === aiField when populated. */
  aiFields: Playfield[] = [];

  // Filters
  private bloomFilter!: AdvancedBloomFilter;
  private crtFilter!: CRTFilter;
  private rgbSplit!: RGBSplitFilter;
  private noiseFilter!: NoiseFilter;

  constructor(opts: PixiAppOptions) {
    this.palette = opts.palette;
    this.renderOpts = opts.renderOpts;
    this.postFxSettings = opts.postFx;
    this.renderScale = opts.renderScale;
    this.app = new Application();
  }

  async init(host: HTMLElement): Promise<void> {
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Device pixel ratio gives crisp text + chrome on high-DPI
    // monitors. In Pixi v8, init width/height are LOGICAL dimensions;
    // the framebuffer is sized logical × resolution internally. So we
    // pass (w * renderScale) as the logical space — same as always,
    // every layout calc unchanged — and let resolution carry the DPR.
    // (Passing pre-multiplied dimensions here inflates the logical
    // space by dpr and shrinks the whole scene — the "tiny board in
    // the corner" regression.) Capped at 3 so an unusually high DPR
    // can't blow up framebuffer cost.
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    await this.app.init({
      width: Math.floor(w * this.renderScale),
      height: Math.floor(h * this.renderScale),
      // Transparent canvas — the HTML layer (body color + optional bg.webm
      // video) provides the backdrop so a custom background can show through.
      backgroundAlpha: 0,
      backgroundColor: 0x000000,
      antialias: true,
      resolution: dpr,
      autoDensity: false,
    });
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.zIndex = '1';
    host.appendChild(canvas);

    // Stage hierarchy. fxRoot collects every layer that gets the
    // global post-fx (bloom / CRT / grain). hudRoot is a sibling
    // unfiltered overlay where each playfield's text + chrome lives
    // so HUD readouts stay crisp.
    this.fxRoot.addChild(this.bgStage, this.playStage, this.fxStage);
    this.app.stage.addChild(this.fxRoot, this.hudRoot);
    // bgSprite is added lazily by redrawBackground.

    // Build playfields
    const playfieldOpts: PlayfieldOptions = {
      cellPx: 32, previewCount: 5,
      showHold: true, showNext: true, showHud: true,
      compact: false,
    };
    this.playerField = new Playfield(this.app, this.palette, playfieldOpts, this.renderOpts);
    this.playStage.addChild(this.playerField.root);
    // Re-parent the playfield's HUD + accolades layers out of the
    // filtered scene so text isn't washed out by bloom/grain. Per-
    // frame sync (in update()) keeps them locked to root.x/y.
    this.hudRoot.addChild(this.playerField.hudLayer);
    this.hudRoot.addChild(this.playerField.accoladesLayer);

    // Filters
    this.bloomFilter = new AdvancedBloomFilter({
      threshold: this.postFxSettings.bloomThreshold,
      bloomScale: this.postFxSettings.bloom,
      brightness: 1.0,
      blur: 8,
      quality: 4,
    });
    this.crtFilter = new CRTFilter({
      curvature: this.postFxSettings.curvature * 10,
      lineWidth: this.postFxSettings.scanlineSize,
      lineContrast: this.postFxSettings.crt * 0.5,
      verticalLine: false,
      noise: 0,
      noiseSize: 1,
      seed: Math.random(),
      vignetting: 0.3 + this.postFxSettings.vignette * 0.4,
      vignettingAlpha: this.postFxSettings.vignette,
      vignettingBlur: 0.6,
      time: 0,
    });
    this.rgbSplit = new RGBSplitFilter({
      red: { x: this.postFxSettings.chromatic * 6, y: 0 },
      green: { x: 0, y: 0 },
      blue: { x: -this.postFxSettings.chromatic * 6, y: 0 },
    });
    this.noiseFilter = new NoiseFilter({ noise: this.postFxSettings.grain * 0.35, seed: Math.random() });
    this.applyFilters();

    this.resize(w, h);
    this.ready = true;
  }

  /** Copy each playfield root's x/y (which includes shake offsets,
   *  layout positioning, etc.) onto the detached HUD + accolades
   *  layers parented to hudRoot. Keeps the unfiltered overlay
   *  locked to its filtered playfield. */
  private syncHudToRoots(): void {
    const pf = this.playerField;
    pf.hudLayer.x = pf.root.x;
    pf.hudLayer.y = pf.root.y;
    pf.accoladesLayer.x = pf.root.x;
    pf.accoladesLayer.y = pf.root.y;
    for (const f of this.aiFields) {
      f.hudLayer.x = f.root.x;
      f.hudLayer.y = f.root.y;
      f.accoladesLayer.x = f.root.x;
      f.accoladesLayer.y = f.root.y;
    }
  }

  private applyFilters(): void {
    if (!this.postFxSettings.enabled) {
      this.fxRoot.filters = null;
      this.bgStage.filters = null;
      this.playerField.setChromaticFilter(null);
      for (const f of this.aiFields) f.setChromaticFilter(null);
      return;
    }
    // Bloom + CRT + noise apply to the fxRoot subtree only. HUD text +
    // accolades live in hudRoot (a sibling outside this chain) so the
    // gameplay readouts stay crisp regardless of effect intensity.
    this.fxRoot.filters = [
      this.bloomFilter, this.crtFilter, this.noiseFilter,
    ];
    // Chromatic aberration scopes to the procedural background and to each
    // playfield's ink layer (cells / ghost / active piece / row flashes /
    // attack effects). HUD text, board frame outlines, and accolade text
    // sit outside inkLayer so they stay crisp regardless of intensity.
    this.bgStage.filters = [this.rgbSplit];
    this.playerField.setChromaticFilter(this.rgbSplit);
    for (const f of this.aiFields) f.setChromaticFilter(this.rgbSplit);
  }

  setPostFxSettings(s: PostFxSettings): void {
    this.postFxSettings = s;
    if (!this.bloomFilter) return;
    this.bloomFilter.threshold = s.bloomThreshold;
    this.bloomFilter.bloomScale = s.bloom * 1.2;
    this.bloomFilter.blur = 8 * Math.max(0.4, s.bloom);
    this.crtFilter.curvature = s.curvature * 10;
    this.crtFilter.lineWidth = s.scanlineSize;
    this.crtFilter.lineContrast = s.crt * 0.5;
    this.crtFilter.vignetting = 0.3 + s.vignette * 0.4;
    this.crtFilter.vignettingAlpha = s.vignette;
    this.rgbSplit.red = { x: s.chromatic * 6, y: 0 };
    this.rgbSplit.blue = { x: -s.chromatic * 6, y: 0 };
    this.noiseFilter.noise = s.grain * 0.35;
    this.applyFilters();
  }

  setRenderScale(scale: number): void {
    if (Math.abs(scale - this.renderScale) < 0.001) return;
    this.renderScale = scale;
    this.resize(window.innerWidth, window.innerHeight);
  }

  setPalette(p: ThemePalette): void {
    this.palette = p;
    this.playerField.setPalette(p);
    for (const f of this.aiFields) f.setPalette(p);
    this.app.renderer.background.color = parseInt(p.bg.slice(1), 16);
    this.redrawBackground();
  }

  setRenderOptions(o: RenderOptions): void {
    this.renderOpts = o;
    this.playerField.setRenderOptions(o);
    for (const f of this.aiFields) f.setRenderOptions(o);
  }

  setLayoutMode(mode: LayoutMode): void {
    if (mode === this.layoutMode) return;
    this.layoutMode = mode;
    const targetCount = mode === 'versus' ? 1 : mode === 'triple' ? 2 : mode === 'royal' ? 3 : 0;
    // Tear down extra AI fields when shrinking layout.
    while (this.aiFields.length > targetCount) {
      const f = this.aiFields.pop()!;
      f.destroy();
    }
    // Spawn additional AI fields when expanding.
    while (this.aiFields.length < targetCount) {
      const newField = new Playfield(this.app, this.palette, {
        cellPx: 20, previewCount: 5,
        showHold: true, showNext: true, showHud: true,
        compact: true,
      }, this.renderOpts);
      this.playStage.addChild(newField.root);
      // Pull AI HUD + accolades into the unfiltered overlay too.
      this.hudRoot.addChild(newField.hudLayer);
      this.hudRoot.addChild(newField.accoladesLayer);
      this.aiFields.push(newField);
    }
    this.aiField = this.aiFields[0] ?? null;
    // Player cell size scales down as more boards crowd the screen.
    const playerCell = mode === 'single' ? 32 : mode === 'versus' ? 24 : mode === 'triple' ? 22 : 20;
    this.playerField.setOptions({ cellPx: playerCell, compact: mode !== 'single' });
    // Re-apply post-fx so newly-spawned AI fields get the chromatic filter
    // bound to their ink layer (otherwise only the player's would split).
    this.applyFilters();
    this.resize(window.innerWidth, window.innerHeight);
  }

  attachPlayer(engine: Engine | null): void {
    this.playerField.setEngine(engine);
  }

  attachAi(engine: Engine | null): void {
    if (!this.aiField) return;
    this.aiField.setEngine(engine);
  }

  /** Detach all engines, fade the play layer out, then collapse the layout
   *  back to 'single' (which destroys any extra AI fields). Used when
   *  returning to the main menu so multi-AI battle boards don't ghost
   *  behind the menu after the run ends. Alpha is reset to 1 once the
   *  fade completes so the next game starts visible. */
  fadeAndClearPlayfields(durationMs = 280): void {
    // Stop physics-driven rendering immediately. Engines themselves are
    // cleared by the App; here we just sever Pixi's references so the
    // stamped cells / active piece / HUD stop refreshing during the fade.
    this.playerField.setEngine(null);
    for (const f of this.aiFields) f.setEngine(null);
    gsap.killTweensOf(this.playStage);
    gsap.killTweensOf(this.hudRoot);
    const dur = Math.max(0.08, durationMs / 1000);
    gsap.to(this.playStage, { alpha: 0, duration: dur, ease: 'power2.in' });
    // hudRoot is a sibling of fxRoot (not a descendant of playStage),
    // so it needs its own tween to fade in lockstep — otherwise HUD
    // text hangs in the air after the boards vanish.
    gsap.to(this.hudRoot, {
      alpha: 0, duration: dur, ease: 'power2.in',
      onComplete: () => {
        this.setLayoutMode('single');
        this.playStage.alpha = 1;
        this.hudRoot.alpha = 1;
      },
    });
  }

  /** Snap the play layer back to fully opaque + kill any in-flight fade.
   *  Called when entering a new run so a mid-fade restart still shows up. */
  resetPlayStageAlpha(): void {
    gsap.killTweensOf(this.playStage);
    gsap.killTweensOf(this.hudRoot);
    this.playStage.alpha = 1;
    this.hudRoot.alpha = 1;
  }

  /** Player playfield's bounding rect in viewport pixels — useful for
   *  positioning DOM overlays (now-playing ticker, etc.) directly
   *  underneath / alongside the game board. Returns null when no
   *  playerField is attached. */
  playerBoardViewportRect(): { left: number; top: number; width: number; height: number } | null {
    const pf = this.playerField;
    const ls = pf.layoutStateView;
    if (!ls) return null;
    const scale = this.renderScale;
    return {
      left: (pf.root.x + ls.boardX) / scale,
      top: (pf.root.y + ls.boardY) / scale,
      width: ls.boardW / scale,
      height: ls.boardH / scale,
    };
  }

  /** Next-queue column rect in viewport pixels — anchors the DOM chat
   *  widget directly beneath the previews. */
  playerQueueViewportRect(): { left: number; top: number; width: number; height: number; previewsBottom: number; boardBottom: number; statsBottom: number } | null {
    const pf = this.playerField;
    const ls = pf.layoutStateView;
    if (!ls) return null;
    const scale = this.renderScale;
    return {
      left: (pf.root.x + ls.nextX) / scale,
      top: (pf.root.y + ls.nextY) / scale,
      width: ls.nextW / scale,
      height: ls.boardH / scale,
      previewsBottom: (pf.root.y + ls.queueBottom) / scale,
      boardBottom: (pf.root.y + ls.boardY + ls.boardH) / scale,
      statsBottom: (pf.root.y + ls.metricsBottom) / scale,
    };
  }

  /** Player sidebar's left edge in viewport pixels — i.e., the left
   *  edge of the hold box. Useful for placing DOM overlays (brand
   *  logo, etc.) flush with the start of the HUD column. */
  playerSidebarLeft(): number | null {
    const pf = this.playerField;
    const ls = pf.layoutStateView;
    if (!ls) return null;
    return (pf.root.x + ls.holdX) / this.renderScale;
  }

  /** Attach a list of AI engines to the existing aiFields (must already
   *  be allocated via setLayoutMode). engines.length should equal
   *  aiFields.length; extras are ignored. */
  attachAis(engines: Engine[]): void {
    for (let i = 0; i < this.aiFields.length; i++) {
      this.aiFields[i]!.setEngine(engines[i] ?? null);
    }
  }

  aiFieldAt(index: number): Playfield | null {
    return this.aiFields[index] ?? null;
  }

  /** Launch a glowing slab from one playfield toward the other to visualize
   *  garbage being sent. The slab pulses with bloom-friendly accent color
   *  and rumbles the receiver when it lands. AI endpoints use the supplied
   *  index; player endpoints ignore it. */
  sendLineAttack(
    from: 'player' | 'ai',
    to: 'player' | 'ai',
    lineCount: number,
    fromAiIndex = 0,
    toAiIndex = 0,
  ): void {
    if (this.layoutMode === 'single' || this.aiFields.length === 0 || lineCount <= 0) return;
    const fieldFor = (kind: 'player' | 'ai', idx: number) =>
      kind === 'player' ? this.playerField : (this.aiFields[idx] ?? this.aiFields[0]!);
    const src = fieldFor(from, fromAiIndex);
    const dst = fieldFor(to, toAiIndex);
    if (src === dst) return;
    const srcLs = src.layoutStateView;
    const dstLs = dst.layoutStateView;
    // Decide direction by comparing the board centers in stage coords so
    // AI→AI trajectories also resolve correctly (player isn't always left
    // of every AI when there are multiple AIs to choose from).
    const srcCenterX = src.root.x + srcLs.boardX + srcLs.boardW / 2;
    const dstCenterX = dst.root.x + dstLs.boardX + dstLs.boardW / 2;
    const srcOnRight = srcCenterX > dstCenterX;
    // Edges of source / dest that face each other.
    const srcEdge = src.root.x + srcLs.boardX + (srcOnRight ? 0 : srcLs.boardW);
    const dstEdge = dst.root.x + dstLs.boardX + (srcOnRight ? dstLs.boardW : 0);
    const dir = srcOnRight ? -1 : 1;
    // Y near the bottom of each board so the slab reads as "ejected from
    // the bottom of the stack". Use each side's own board height so AI→AI
    // attacks between boards of different size still land cleanly.
    const ySrc = src.root.y + srcLs.boardH * 0.78;
    const yDst = dst.root.y + dstLs.boardH * 0.78;
    // Visual: a thin neon bar with a brighter core; height scales with lineCount.
    const slab = new Container();
    const w = Math.min(120, 28 + lineCount * 14);
    const h = Math.max(10, 4 + lineCount * 2);
    const accentHex = parseInt(this.palette.accent.replace('#', '').slice(0, 6), 16);
    const accent2Hex = parseInt(this.palette.accent2.replace('#', '').slice(0, 6), 16);
    const glow = new Graphics();
    glow.rect(-w / 2, -h * 1.2, w, h * 2.4).fill({ color: accentHex, alpha: 0.32 });
    const core = new Graphics();
    core.rect(-w / 2, -h / 2, w, h).fill({ color: accent2Hex, alpha: 1 });
    core.rect(-w / 2 + 2, -h / 2 + 2, w - 4, h - 4).fill({ color: 0xffffff, alpha: 0.92 });
    slab.addChild(glow, core);
    slab.x = srcEdge;
    slab.y = ySrc;
    slab.scale.set(0.6, 1);
    slab.alpha = 0;
    this.fxStage.addChild(slab);
    // Spawn flicker → travel → impact.
    const travel = Math.abs(dstEdge - srcEdge);
    const dur = Math.max(0.22, Math.min(0.55, travel / 1400));
    gsap.timeline()
      .to(slab, { alpha: 1, duration: 0.06, ease: 'power2.out' })
      .to(slab.scale, { x: 1.0, duration: 0.08, ease: 'power2.out' }, '<')
      .to(slab, { x: dstEdge, y: yDst, duration: dur, ease: 'power2.in' }, 0.06)
      .to(slab.scale, { x: 0.4, y: 1.6, duration: 0.10, ease: 'power3.in' }, `>-0.08`)
      .to(slab, { alpha: 0, duration: 0.10, ease: 'power2.in' }, '<')
      .call(() => {
        slab.destroy({ children: true });
        // Small impact shake on the receiving field, scales with line count.
        const punch = Math.min(7, 2 + lineCount * 0.8);
        dst.shake(punch, 220);
        // Pulse the inner board outline to telegraph the incoming garbage.
        dst.flashBoardRim(this.palette.accent, 320);
      });
    // Faint streak following the slab — line of dimmer rectangles.
    for (let i = 1; i <= 4; i++) {
      const ghost = new Graphics();
      ghost.rect(-w / 2, -h / 2, w, h)
        .fill({ color: accentHex, alpha: 0.18 - i * 0.03 });
      ghost.x = srcEdge - i * 18 * dir;
      ghost.y = ySrc;
      ghost.scale.x = 0.7;
      this.fxStage.addChild(ghost);
      gsap.to(ghost, {
        x: dstEdge - i * 18 * dir, y: yDst,
        alpha: 0, duration: dur, ease: 'power2.in',
        onComplete: () => ghost.destroy({ children: true }),
      });
    }
  }

  resize(w: number, h: number): void {
    this.lastCssW = w;
    this.lastCssH = h;
    // Mirror init(): renderer.resize takes LOGICAL dimensions; the
    // third argument re-applies the current DPR as the resolution so
    // the framebuffer follows browser-zoom / monitor changes. CSS
    // dimensions stay at the unscaled viewport size.
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    this.app.renderer.resize(
      Math.floor(w * this.renderScale),
      Math.floor(h * this.renderScale),
      dpr,
    );
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    // Recompute cellPx so the board fits
    this.recomputeCellSizes(w * this.renderScale, h * this.renderScale);
    // Re-layout
    this.layoutPlayfields(w * this.renderScale, h * this.renderScale);
    this.redrawBackground();
  }

  private recomputeCellSizes(w: number, h: number): void {
    const margin = 24 * this.renderScale;
    const sidebarW = 200 * this.renderScale;
    // DOM chrome (miniplayer + chat column) rides the playfield's right
    // edge; reserve its width so the board scales down rather than
    // pushing the column off-screen on smaller windows.
    const gutter = this.domGutterRight * this.renderScale;
    if (this.layoutMode === 'single') {
      const availW = w - margin * 2 - sidebarW * 2 - gutter;
      const availH = h - margin * 2;
      const byW = Math.floor(availW / BOARD_WIDTH);
      const byH = Math.floor(availH / BOARD_VISIBLE_HEIGHT);
      const cellPx = Math.max(14, Math.min(byW, byH, 34));
      this.playerField.setOptions({ cellPx });
    } else if (this.layoutMode === 'versus') {
      const gap = 60 * this.renderScale;
      const half = (w - margin * 2 - gap - gutter) / 2;
      const availW = half - sidebarW * 1.4;
      const availH = h - margin * 2;
      const byW = Math.floor(availW / BOARD_WIDTH);
      const byH = Math.floor(availH / BOARD_VISIBLE_HEIGHT);
      const cellPx = Math.max(12, Math.min(byW, byH, 26));
      this.playerField.setOptions({ cellPx });
      for (const f of this.aiFields) f.setOptions({ cellPx });
    } else {
      // Multi-AI modes (triple = 2 AIs, royal = 3 AIs). Player gets a
      // larger board on the left; AIs cluster smaller to the right.
      const gap = 36 * this.renderScale;
      const aiBoardsW = (this.aiFields.length * BOARD_WIDTH) + (this.aiFields.length - 1) * 2;
      // Solve cellPx so player + gap + (aiCount * (4-cell sidebar + 10-cell board + 4-cell sidebar)) fits.
      const availH = h - margin * 2;
      const byH = Math.floor(availH / BOARD_VISIBLE_HEIGHT);
      // Roughly the available width minus the player chunk + gaps.
      const playerCells = BOARD_WIDTH + 8; // 4 cells hold + 10 board + 4 next
      const aiCellsPer  = BOARD_WIDTH + 8;
      const totalCells = playerCells + aiCellsPer * this.aiFields.length;
      const availW = w - margin * 2 - gap * this.aiFields.length - gutter;
      const byW = Math.floor(availW / totalCells);
      const cellPx = Math.max(11, Math.min(byW, byH, 22));
      // Player gets a slight size bump over each AI for visual hierarchy.
      const aiCellPx = Math.max(10, Math.round(cellPx * 0.92));
      this.playerField.setOptions({ cellPx });
      for (const f of this.aiFields) f.setOptions({ cellPx: aiCellPx });
      void aiBoardsW;
    }
  }

  /** Width (CSS px) of the DOM chrome column (miniplayer + chat)
   *  anchored to the playfield's right side. The board assembly shifts
   *  left by half of it so the FULL composition — sidebar through chat
   *  — reads centered on the page, not just the Pixi part. */
  private domGutterRight = 0;
  private lastCssW = 0;
  private lastCssH = 0;
  setDomGutterRight(px: number): void {
    const v = Math.max(0, Math.round(px));
    if (v === this.domGutterRight) return;
    this.domGutterRight = v;
    if (this.lastCssW > 0) this.resize(this.lastCssW, this.lastCssH);
  }

  private layoutPlayfields(w: number, h: number): void {
    const p = this.playerField.layout();
    // Shift the whole assembly left by half the DOM gutter so the
    // composition (boards + DOM column) centers as one unit. Clamped
    // so the playfield never leaves the viewport on narrow windows.
    const margin = 24 * this.renderScale;
    const shift = Math.floor(this.domGutterRight * this.renderScale / 2);
    if (this.layoutMode === 'single') {
      const x = Math.max(margin, Math.floor((w - p.width) / 2) - shift);
      const y = Math.floor((h - p.height) / 2);
      this.playerField.setBasePosition(x, y);
    } else if (this.layoutMode === 'versus' && this.aiField) {
      const a = this.aiField.layout();
      const gap = 60 * this.renderScale;
      const totalW = p.width + gap + a.width;
      const startX = Math.max(margin, Math.floor((w - totalW) / 2) - shift);
      const y = Math.floor((h - p.height) / 2);
      this.playerField.setBasePosition(startX, y);
      this.aiField.setBasePosition(startX + p.width + gap, y);
    } else if ((this.layoutMode === 'triple' || this.layoutMode === 'royal') && this.aiFields.length > 0) {
      // Lay player flush left of center, then the AI fields in a row to
      // the right with smaller gaps between them than between player+AIs.
      const gap = 36 * this.renderScale;
      const aiGap = 18 * this.renderScale;
      let totalW = p.width;
      const aiLayouts = this.aiFields.map((f) => f.layout());
      for (const a of aiLayouts) totalW += gap + a.width;
      totalW -= (this.aiFields.length - 1) * (gap - aiGap);
      const startX = Math.max(margin, Math.floor((w - totalW) / 2) - shift);
      const y = Math.floor((h - p.height) / 2);
      this.playerField.setBasePosition(startX, y);
      let cx = startX + p.width + gap;
      for (let i = 0; i < this.aiFields.length; i++) {
        this.aiFields[i]!.setBasePosition(cx, y);
        cx += aiLayouts[i]!.width + aiGap;
      }
    }
  }

  private redrawBackground(): void {
    // Lazy-create the sprite once. We never destroy + recreate it on
    // palette switches because that can leave a frame with the sprite
    // missing from the scene graph — the source of the user's "background
    // sometimes stops displaying when switching themes" report.
    if (!this.bgSprite) {
      this.bgSprite = new Sprite(Texture.EMPTY);
      this.bgStage.addChild(this.bgSprite);
    }
    // Regenerate the texture only when the palette actually changes.
    if (this.bgTextureFor !== this.palette.id) {
      const newTex = generateBackdropTexture(this.app, this.palette);
      const oldTex = this.bgTexture;
      // Swap the sprite's texture BEFORE destroying the old one so the
      // sprite never holds a dangling reference.
      this.bgSprite.texture = newTex;
      this.bgTexture = newTex;
      this.bgTextureFor = this.palette.id;
      // Defer the old-texture destroy so any in-flight reference completes first.
      if (oldTex) queueMicrotask(() => { try { oldTex.destroy(true); } catch { /* */ } });
    }
    // Oversize the sprite by 8% so we can slowly drift it across the canvas
    // without exposing edges — adds gentle ambient motion to the otherwise
    // static procedural gradient. Drift is driven in update().
    // canvas.width/height are framebuffer pixels; we want LOGICAL
    // dimensions for the sprite so it covers the visible canvas at
    // any DPR. screen.width / .height give logical units directly.
    const w = this.app.renderer.screen.width;
    const h = this.app.renderer.screen.height;
    this.bgSprite.width = w * 1.12;
    this.bgSprite.height = h * 1.12;
    // Initial centered offset; update() animates around this.
    this.bgSprite.x = -w * 0.06;
    this.bgSprite.y = -h * 0.06;
  }

  private bgDriftTime = 0;

  /** Per-frame update. Ticker is driven externally so the engine update can stay in lockstep. */
  update(dtMs: number): void {
    if (!this.ready) return;
    this.playerField.update(dtMs);
    for (const f of this.aiFields) f.update(dtMs);
    // Mirror each playfield root's position onto its detached HUD +
    // accolades layers (they live under hudRoot, outside the filter
    // chain, so they don't inherit root's transform automatically).
    // Cheap to run unconditionally — just a handful of float copies.
    this.syncHudToRoots();
    // Advance CRT shader time so the scanline noise drifts. We also
    // pulse lineWidth + lineContrast slowly so the scanlines aren't
    // mechanically uniform — feels closer to an unsteady tube than a
    // ruler-grid screen overlay.
    if (this.crtFilter) {
      this.crtFilter.time += dtMs / 1000;
      const baseLine = this.postFxSettings.scanlineSize;
      const baseContrast = this.postFxSettings.crt * 0.5;
      const t = this.crtFilter.time;
      // Two layered sine waves at slow rates produce a non-repeating
      // wobble. Amplitudes scaled down so the effect is subtle.
      this.crtFilter.lineWidth = baseLine * (1 + Math.sin(t * 0.37) * 0.10 + Math.sin(t * 0.91) * 0.05);
      this.crtFilter.lineContrast = baseContrast * (1 + Math.sin(t * 0.62 + 1.3) * 0.18);
      // Very small noise drift in the CRT plate itself for organic grit.
      this.crtFilter.noise = Math.max(0, this.postFxSettings.grain * 0.18 + Math.sin(t * 1.7) * 0.02);
      this.crtFilter.noiseSize = 1 + Math.sin(t * 0.43) * 0.4;
    }
    if (this.noiseFilter) {
      // Film grain: jitter the seed every frame for moving pixels, but
      // also slowly modulate intensity so static grain becomes a
      // 'breathing' grain field.
      this.noiseFilter.seed = Math.random();
      const grainBase = this.postFxSettings.grain * 0.35;
      const t = (this.crtFilter?.time ?? 0);
      this.noiseFilter.noise = grainBase * (0.85 + Math.sin(t * 0.78) * 0.15);
    }
    // Gentle ambient drift on the procedural background — Lissajous-
    // style path with a long period so it never feels noisy. Amplitude
    // sits within the 12% oversize margin so no edge ever shows.
    // Subtle scale breath added on top of the translation for extra life.
    if (this.bgSprite) {
      this.bgDriftTime += dtMs / 1000;
      // screen.width / .height are logical units (framebuffer / DPR).
      // The bgSprite's coords are logical, so we match the space.
      const w = this.app.renderer.screen.width;
      const h = this.app.renderer.screen.height;
      const amp = 0.05; // % of canvas dimension to drift
      const t = this.bgDriftTime;
      this.bgSprite.x = -w * 0.06 + Math.sin(t * 0.09) * (w * amp);
      this.bgSprite.y = -h * 0.06 + Math.cos(t * 0.07) * (h * amp);
    }
  }
}

// ─── Procedural backdrop generator ──────────────────────────────────
//
// Renders a deterministic noise-warped gradient using each palette's
// bg / bg2 / accent / accent2 colors. The seed is derived from the
// palette id so the same theme always produces the same backdrop.
// Output is a 1024x576 (16:9) Texture — Pixi stretches it to the
// viewport which is fine because the contents are low-frequency.

function generateBackdropTexture(app: import('pixi.js').Application, palette: import('../render/palettes').ThemePalette): Texture {
  const W = 1024, H = 576;
  // Deterministic seed from palette id.
  let seed = 0;
  for (let i = 0; i < palette.id.length; i++) seed = (seed * 31 + palette.id.charCodeAt(i)) | 0;
  // Two fbm fields used to warp the gradient coordinates, plus a third for
  // a soft cloud overlay.
  const warpA = fbm(seed ^ 0x9e3779b9, 4);
  const warpB = fbm(seed ^ 0x85ebca6b, 4);
  const cloud = fbm(seed ^ 0xc2b2ae35, 5);

  const hex = (c: string) => {
    const v = parseInt(c.replace('#', '').slice(0, 6), 16);
    return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff] as [number, number, number];
  };
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const lerp3 = (a: [number, number, number], b: [number, number, number], t: number): [number, number, number] =>
    [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

  const bg = hex(palette.bg);
  const bg2 = hex(palette.bg2);
  const accent = hex(palette.accent);
  const accent2 = hex(palette.accent2);

  // Build the pixel buffer.
  const buf = new Uint8ClampedArray(W * H * 4);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      // Normalized coords.
      const nx = x / W;
      const ny = y / H;
      // Warp UVs with two fbm fields. Strength is gentle so the gradient
      // reads as "wavy" rather than abstract.
      const wx = warpA(nx * 2.6, ny * 2.6) - 0.5;
      const wy = warpB(nx * 2.6, ny * 2.6) - 0.5;
      const u = nx + wx * 0.45;
      const v = ny + wy * 0.45;
      // Vertical gradient bg → bg2, then add big soft auroras of accent colour.
      const t = Math.max(0, Math.min(1, v));
      let c = lerp3(bg, bg2, t);
      // Primary accent aurora — large, soft, biased toward top-left.
      const blob = cloud(u * 1.0 + 0.6, v * 1.0 + 0.2);
      const accentMix = Math.pow(Math.max(0, blob - 0.40), 1.2) * 0.85;
      c = lerp3(c, accent, accentMix);
      // Secondary accent aurora — opposite corner.
      const blob2 = cloud(u * 1.4 - 0.3, v * 1.4 - 0.5);
      const accent2Mix = Math.pow(Math.max(0, blob2 - 0.45), 1.3) * 0.65;
      c = lerp3(c, accent2, accent2Mix);
      // Soft vignette toward edges.
      const cx = nx - 0.5, cy = ny - 0.5;
      const d = Math.sqrt(cx * cx + cy * cy);
      const vig = 1 - Math.min(1, d * 0.9);
      c = [c[0] * (0.70 + 0.30 * vig), c[1] * (0.70 + 0.30 * vig), c[2] * (0.70 + 0.30 * vig)];
      const i = (y * W + x) * 4;
      buf[i + 0] = c[0];
      buf[i + 1] = c[1];
      buf[i + 2] = c[2];
      buf[i + 3] = 255;
    }
  }
  // Wrap in a canvas → Pixi Texture.
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx2d = canvas.getContext('2d')!;
  const imageData = ctx2d.createImageData(W, H);
  imageData.data.set(buf);
  ctx2d.putImageData(imageData, 0, 0);
  return Texture.from(canvas);
}
